import * as yup from 'yup';
import { NumberSchema } from 'yup';
import { Type } from '../constants/entitySpec';
import { EntitySpecExtension, SchemaDefinitionExtension } from '../interfaces';
import {
  entityId,
  externalReferences,
  keyValuePair,
  labelValuePair,
  oneOfEnum,
  percentage,
  telephoneNumber,
  uniqueArray,
  uniqueArrayWithMultipleKeys
} from './yup.validations';

export function configureYup(yupToConfigure: typeof yup = yup) {
  yupToConfigure.setLocale({
    mixed: {
      required: '${path} is a required field',
      notType: '${path} is not a valid ${type}'
    },
    number: {
      integer: '${path} must be a number'
    },
    string: {
      max: '${path} is too long (max: ${max})',
      min: '${path} is too short (min: ${min})'
    }
  });

  yupToConfigure.addMethod<
    yup.ArraySchema<{ Type: string; Value: string }[] | undefined, any>
  >(yupToConfigure.array, 'externalReferences', externalReferences);
  yupToConfigure.addMethod<yup.MixedSchema>(
    yupToConfigure.mixed,
    'oneOfEnum',
    oneOfEnum
  );
  yupToConfigure.addMethod<yup.NumberSchema>(
    yupToConfigure.number,
    'entityId',
    entityId
  );
  yupToConfigure.addMethod<yup.NumberSchema>(
    yupToConfigure.number,
    'percentage',
    percentage
  );
  yupToConfigure.addMethod<yup.ObjectSchema<any>>(
    // @ts-expect-error Yup's object factory is not assignable to addMethod under TypeScript 7.
    yupToConfigure.object,
    'keyValuePair',
    keyValuePair
  );
  yupToConfigure.addMethod<yup.ObjectSchema<any>>(
    // @ts-expect-error Yup's object factory is not assignable to addMethod under TypeScript 7.
    yupToConfigure.object,
    'labelValuePair',
    labelValuePair
  );
  yupToConfigure.addMethod<yup.StringSchema>(
    yupToConfigure.string,
    'telephoneNumber',
    telephoneNumber
  );
  yupToConfigure.addMethod(yupToConfigure.array, 'uniqueArray', uniqueArray);
  yupToConfigure.addMethod(
    yupToConfigure.array,
    'uniqueArrayWithMultipleKeys',
    uniqueArrayWithMultipleKeys
  );
}

/**
 * Build a YUP schema validation from a SchemaDefinition https://github.com/jquense/yup
 */
export function buildYupSchemaFromEntitySpec(
  schema: null,
  definitions?: undefined,
  parentKey?: undefined
): null;
export function buildYupSchemaFromEntitySpec(
  schema: EntitySpecExtension,
  definitions?: EntitySpecExtension['Schema']['x-definitions'],
  parentKey?: string
): yup.AnySchema;
export function buildYupSchemaFromEntitySpec(
  schema: EntitySpecExtension | null,
  definitions?: EntitySpecExtension['Schema']['x-definitions'],
  parentKey?: string
): any {
  if (!schema) {
    return null;
  }
  if (!schema.Schema.properties && schema.Schema.type) {
    //  @ts-ignore
    return getTypeFromEntitySpec(schema.Schema);
  }
  const currentDefinition = definitions || schema.Schema['x-definitions'];
  const result: { [key: string]: any } = {};

  Object.entries(schema.Schema.properties).forEach(([key, value]) => {
    const isRequired =
      !value.apiGenerated && schema.Schema.required?.includes(key);
    const thisParentKey = parentKey ? `${parentKey}.${key}` : key;
    if (
      value.type === Type.array &&
      value.items?.type !== undefined &&
      value.items?.type !== Type.object &&
      value.items?.type !== Type.array
    ) {
      // @ts-ignore
      result[key] = yup.array().of(getTypeFromEntitySpec(value.items));
    } else if (value.type === Type.array && value.items) {
      result[key] = yup.array().of(
        // @ts-ignore
        buildYupSchemaFromEntitySpec(
          {
            /**
             * value.items can have a $ref or an schema of type object or array. If value.items is schema of primary type, first if condition
             * handles the case.
             */
            Schema: (value.items?.$ref
              ? currentDefinition?.[key]!
              : value.items) as EntitySpecExtension['Schema']
          },
          currentDefinition,
          thisParentKey
        )
      );
    } else if ('$ref' in value) {
      result[key] = buildYupSchemaFromEntitySpec(
        {
          Schema: currentDefinition?.[key]!
        },
        currentDefinition,
        thisParentKey
      );
    } else if (
      // @ts-ignore : TODO : Implement logic for anyOf, (Currently this is workaround for values with multiple different types)
      value?.anyOf
    ) {
      result[key] = yup.mixed();
    } else {
      result[key] = getTypeFromEntitySpec(value);
    }
    if (isRequired) {
      result[key] = result[key].required();
    }
    if (!isRequired && value.nullable && result[key]) {
      result[key] = result[key].nullable();
    }
    if (value.dependentField) {
      const thisSchema = result[key];
      result[key] = yup
        .mixed()
        .when(
          [
            value.dependentField,
            parentKey
              ? `$${parentKey}.${value.dependentField}`
              : `$${value.dependentField}`
          ],
          {
            is: (
              dependentFieldCastedValue: any,
              dependentFieldContextValue: any
            ) => {
              const dependentFieldValue: any =
                dependentFieldContextValue ?? dependentFieldCastedValue;
              if (value.requiredDependentFieldValues) {
                return value.requiredDependentFieldValues.includes(
                  dependentFieldValue
                );
              }
              return (
                dependentFieldValue !== undefined && dependentFieldValue !== ''
              );
            },
            then: () => thisSchema.label(value.title)
          }
        )
        .label(value.title);
    }
    result[key] = result[key].label(value.title);
  });

  return yup.object().shape(result);
}

function getTypeFromEntitySpec(schema: SchemaDefinitionExtension) {
  let result:
    | yup.MixedSchema
    | yup.StringSchema<string | null | undefined, object>
    | yup.NumberSchema<number | null | undefined, object>
    | yup.BooleanSchema<boolean | null | undefined, object>;
  if (schema.type === Type.integer) {
    result = yup.number().integer(`${schema.title} must be an integer`);
  } else if (schema.type === Type.number) {
    result = yup.number();
  } else if (schema.type === Type.boolean) {
    result = yup.bool();
  } else {
    result =
      'maxlength' in schema && schema.maxlength
        ? yup.string().max(schema.maxlength as number)
        : yup.string();
  }

  if (
    result instanceof NumberSchema &&
    (schema.type === Type.integer || schema.type === Type.number)
  ) {
    if (typeof schema.minimum === 'number') {
      result = result.min(
        schema.minimum,
        `${schema.title} must be ${schema.minimum} or greater`
      );
    }
    if (typeof schema.maximum === 'number') {
      result = result.max(
        schema.maximum,
        `${schema.title} must be ${schema.maximum} or less`
      );
    }
  }

  return result;
}
